import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DollarBillIcon } from '@/components/DollarBillIcon';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Home, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';

export const OnboardingHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveAndGoToDashboard = async () => {
    setIsSaving(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Get current progress
      const { data: progress } = await supabase
        .from('onboarding_progress')
        .select('sections_completed, data_snapshot, current_section')
        .eq('company_id', companyId)
        .single();

      if (!progress) {
        // No progress record exists, create one
        await supabase
          .from('onboarding_progress')
          .insert({
            company_id: companyId,
            current_section: 1,
            sections_completed: {},
            last_activity: new Date().toISOString(),
          });
      } else {
        // Check if we're in Phase 2
        const isPhase2 = location.pathname.includes('/onboarding/phase-2');
        
        if (isPhase2) {
          // Phase 2: Save current section progress
          const sectionsCompleted = (progress.sections_completed as any) || {};
          const phase2Sections = sectionsCompleted.phase2 || {};
          
          // Determine which section we're in based on the route
          let currentSectionKey: string | null = null;
          if (location.pathname.includes('/bank-accounts')) {
            currentSectionKey = 'bank_accounts_phase2';
          } else if (location.pathname.includes('/cogs-vendor')) {
            currentSectionKey = 'cogs_vendor_training';
          } else if (location.pathname.includes('/pos')) {
            currentSectionKey = 'pos_reports';
          }

          // Mark current section as in_progress if not already completed
          if (currentSectionKey && (!phase2Sections[currentSectionKey] || phase2Sections[currentSectionKey].status !== 'completed')) {
            phase2Sections[currentSectionKey] = {
              ...phase2Sections[currentSectionKey],
              status: 'in_progress',
              lastSaved: new Date().toISOString(),
            };

            await supabase
              .from('onboarding_progress')
              .update({
                sections_completed: {
                  ...sectionsCompleted,
                  phase2: phase2Sections,
                },
                last_activity: new Date().toISOString(),
              })
              .eq('company_id', companyId);
          } else {
            // Just update last_activity
            await supabase
              .from('onboarding_progress')
              .update({
                last_activity: new Date().toISOString(),
              })
              .eq('company_id', companyId);
          }
        } else {
          // Phase 1 or hub: Update last_activity to mark that progress was saved
          // The context should have already saved the current section state
          await supabase
            .from('onboarding_progress')
            .update({
              last_activity: new Date().toISOString(),
            })
            .eq('company_id', companyId);
        }
      }

      toast({
        title: 'Progress Saved',
        description: 'Your progress has been saved successfully.',
      });

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error saving progress:', error);
      toast({
        title: 'Error Saving Progress',
        description: error.message || 'Failed to save progress. You can continue later.',
        variant: 'destructive',
      });
      // Still navigate to dashboard even if save fails
      navigate('/dashboard');
    } finally {
      setIsSaving(false);
      setShowDialog(false);
    }
  };

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-background/80 backdrop-blur-sm border-b border-border"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-center gap-3">
              <DollarBillIcon size={40} />
              <span className="text-2xl font-bold">
                <span className="text-foreground">Dollar</span>{" "}
                <span className="text-vault-green">Bill</span>
              </span>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowDialog(true)}
              className="gap-2"
            >
              <Home className="h-4 w-4" />
              Go to Dashboard
            </Button>
          </div>
        </div>
      </motion.header>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Progress and Go to Dashboard?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current progress will be saved automatically. You can return to complete the onboarding at any time from the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSaveAndGoToDashboard}
              disabled={isSaving}
              className="gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save & Go to Dashboard'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
